import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  AlertTriangle,
  ArrowUpRight,
  ChevronLeft,
  ChevronRight,
  ClipboardList,
  LayoutDashboard,
  Search,
  Warehouse,
  X,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Badge } from 'components/ui/badge';
import { Button } from 'components/ui/button';
import { Input } from 'components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from 'components/ui/select';
import { Skeleton } from 'components/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from 'components/ui/table';
import { useAppDispatch, useAppSelector } from '../../hooks';
import { openGroupDrawer } from '../../modules/drawer/actions';
import { DBGroupShape } from '../../modules/groups/types';
import CountOrgsByGroup from './CountOrgsByGroup';

interface IProps {
  data: DBGroupShape[];
  isLoading?: boolean;
  hasError?: boolean;
}

function TableList({ data, isLoading = false, hasError = false }: IProps) {
  const dispatch = useAppDispatch();
  const { t, i18n } = useTranslation();
  const uid = useAppSelector((state) => state.user.details.uid);
  const searchKey = `groups:last-search:${uid}`;
  const [search, setSearchValue] = useState(() => {
    try {
      return localStorage.getItem(searchKey) || '';
    } catch {
      return '';
    }
  });
  const setSearch = (value: string) => {
    setSearchValue(value);
    try {
      if (value) localStorage.setItem(searchKey, value);
      else localStorage.removeItem(searchKey);
    } catch {
      // Search still works when browser storage is unavailable.
    }
  };
  const [onlyLowStock, setOnlyLowStock] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const groups = data.map((group) => ({
    ...group,
    hasAlert: Object.values(group.storage || {}).some(
      (item) => item?.storage <= item?.storageAlert
    ),
  }));
  const lowStockCount = groups.filter((group) => group.hasAlert).length;
  const filtered = groups
    .filter(
      (group) =>
        group.title.toLowerCase().includes(search.trim().toLowerCase()) &&
        (!onlyLowStock || group.hasAlert)
    )
    .sort((a, b) => a.title.localeCompare(b.title, i18n.language));
  const pageCount = Math.max(1, Math.ceil(filtered.length / pageSize));
  const currentPage = Math.min(page, pageCount);
  const start = (currentPage - 1) * pageSize;
  const resetFilters = () => {
    setSearch('');
    setOnlyLowStock(false);
    setPage(1);
  };

  if (hasError) {
    return (
      <div role="alert" className="rounded-xl border border-destructive/30 bg-background p-6">
        <h2 className="text-base font-semibold">{t('Grupperne kunne ikke indlæses')}</h2>
        <p className="mt-1 text-muted-foreground">
          {t('Prøv at genindlæse siden. Kontakt en administrator, hvis problemet fortsætter.')}
        </p>
        <Button variant="outline" className="mt-4" onClick={() => window.location.reload()}>
          {t('Genindlæs')}
        </Button>
      </div>
    );
  }

  return (
    <section
      aria-label={t('Gruppeoversigt')}
      aria-busy={isLoading}
      className="overflow-hidden rounded-xl border bg-background shadow-sm"
    >
      <div className="flex flex-col gap-3 border-b p-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="relative w-full sm:max-w-sm">
          <Search
            aria-hidden="true"
            className="pointer-events-none absolute left-3 top-2.5 h-4 w-4 text-muted-foreground"
          />
          <Input
            aria-label={t('Søg efter gruppe')}
            placeholder={t('Søg efter gruppe…')}
            value={search}
            disabled={isLoading}
            onChange={(event) => {
              setSearch(event.target.value);
              setPage(1);
            }}
            className="pl-9 pr-9"
          />
          {search && (
            <Button
              variant="ghost"
              size="icon"
              className="absolute right-0 top-0"
              aria-label={t('Ryd søgning')}
              onClick={() => {
                setSearch('');
                setPage(1);
              }}
            >
              <X aria-hidden="true" />
            </Button>
          )}
        </div>
        <Button
          variant={onlyLowStock ? 'secondary' : 'outline'}
          aria-pressed={onlyLowStock}
          disabled={isLoading}
          className="justify-center"
          onClick={() => {
            setOnlyLowStock(!onlyLowStock);
            setPage(1);
          }}
        >
          <AlertTriangle aria-hidden="true" className="text-amber-600" />
          {t('Lav beholdning')}
          <Badge variant="secondary" className="ml-1 tabular-nums">
            {isLoading ? '—' : lowStockCount}
          </Badge>
        </Button>
      </div>
      {isLoading ? (
        <div role="status" className="space-y-5 p-6">
          <span className="sr-only">{t('Indlæser grupper')}</span>
          {[0, 1, 2, 3, 4].map((row) => (
            <Skeleton key={row} className="h-12 w-full" />
          ))}
        </div>
      ) : filtered.length === 0 ? (
        <div className="flex flex-col items-center px-6 py-16 text-center" role="status">
          <Search aria-hidden="true" className="mb-4 h-8 w-8 text-muted-foreground" />
          <h2 className="text-base font-semibold">
            {t(data.length ? 'Ingen grupper matcher' : 'Ingen grupper endnu')}
          </h2>
          <p className="mt-1 text-sm text-muted-foreground">
            {t(
              data.length
                ? 'Prøv et andet navn eller fjern dine filtre.'
                : 'Tilføj en gruppe for at samle afdelinger og få overblik over lageret.'
            )}
          </p>
          {(search || onlyLowStock) && (
            <Button variant="outline" className="mt-4" onClick={resetFilters}>
              {t('Nulstil filtre')}
            </Button>
          )}
        </div>
      ) : (
        <Table className="block md:table">
          <TableHeader className="hidden bg-muted/40 md:table-header-group">
            <TableRow className="hover:bg-transparent">
              <TableHead className="px-5">{t('Gruppenavn')}</TableHead>
              <TableHead className="text-center">{t('Antal afdelinger')}</TableHead>
              <TableHead className="px-5">{t('Hovedlager status')}</TableHead>
              <TableHead className="px-5 text-right">{t('Handlinger')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody className="block md:table-row-group">
            {filtered.slice(start, start + pageSize).map((group) => (
              <TableRow
                key={group.id}
                className="grid grid-cols-2 gap-x-2 px-4 py-3 md:table-row md:p-0"
              >
                <TableCell className="col-span-2 px-0 py-2 md:px-5 md:py-5">
                  <Link
                    className="inline-flex items-center gap-2 rounded-sm font-medium text-foreground hover:text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    to={`/depts?groupId=${group.id}`}
                  >
                    {group.title}
                    <ArrowUpRight
                      aria-hidden="true"
                      className="h-3.5 w-3.5 shrink-0 text-muted-foreground"
                    />
                  </Link>
                </TableCell>
                <TableCell className="flex items-center gap-2 px-0 tabular-nums md:table-cell md:px-2 md:text-center">
                  <span className="text-muted-foreground md:hidden">{t('header.depts')}:</span>
                  <CountOrgsByGroup groupId={group.id} />
                </TableCell>
                <TableCell className="px-0 md:px-5">
                  {group.hasAlert ? (
                    <Badge
                      variant="outline"
                      className="gap-1.5 whitespace-nowrap border-amber-200 bg-amber-50 text-amber-800"
                    >
                      <AlertTriangle aria-hidden="true" className="h-3.5 w-3.5" />
                      {t('Beholdning lav')}
                    </Badge>
                  ) : (
                    <span className="text-sm text-muted-foreground">
                      —<span className="sr-only">{t('Ingen lageradvarsel registreret')}</span>
                    </span>
                  )}
                </TableCell>
                <TableCell className="col-span-2 px-0 pt-3 md:px-5 md:py-3">
                  <div className="flex flex-wrap items-center gap-1 md:justify-end">
                    <Button asChild variant="ghost" size="sm">
                      <Link
                        to={`/dashboard?groupId=${group.id}`}
                        aria-label={`${t('Dashboard')}: ${group.title}`}
                      >
                        <LayoutDashboard aria-hidden="true" />
                        {t('Dashboard')}
                      </Link>
                    </Button>
                    <Button asChild variant="ghost" size="sm">
                      <Link
                        to={`/groups/${group.id}/audits/templates`}
                        aria-label={`${t('Opgave skabeloner')}: ${group.title}`}
                      >
                        <ClipboardList aria-hidden="true" />
                        {t('Skabeloner')}
                      </Link>
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      aria-label={`${t('Lager')}: ${group.title}`}
                      onClick={() => dispatch(openGroupDrawer({ groupId: group.id }))}
                    >
                      <Warehouse aria-hidden="true" />
                      {t('Lager')}
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
      {!isLoading && filtered.length > 0 && (
        <div className="flex flex-col gap-4 border-t px-4 py-3 text-sm sm:flex-row sm:items-center sm:justify-between">
          <p role="status" className="text-muted-foreground">
            {t('Viser {{from}}–{{to}} af {{count}} grupper', {
              from: start + 1,
              to: Math.min(start + pageSize, filtered.length),
              count: filtered.length,
            })}
          </p>
          <div className="flex flex-wrap items-center gap-3">
            <Select
              value={String(pageSize)}
              onValueChange={(value) => {
                setPageSize(Number(value));
                setPage(1);
              }}
            >
              <SelectTrigger aria-label={t('Grupper pr. side')} className="w-32">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {[10, 20, 50, 100].map((size) => (
                  <SelectItem key={size} value={String(size)}>
                    {t('{{count}} pr. side', { count: size })}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <nav aria-label={t('Gruppesider')} className="flex items-center gap-2">
              <Button
                variant="outline"
                size="icon"
                aria-label={t('Forrige side')}
                disabled={currentPage === 1}
                onClick={() => setPage(currentPage - 1)}
              >
                <ChevronLeft aria-hidden="true" />
              </Button>
              <span className="min-w-[4rem] text-center tabular-nums">
                {t('{{page}} af {{pages}}', { page: currentPage, pages: pageCount })}
              </span>
              <Button
                variant="outline"
                size="icon"
                aria-label={t('Næste side')}
                disabled={currentPage === pageCount}
                onClick={() => setPage(currentPage + 1)}
              >
                <ChevronRight aria-hidden="true" />
              </Button>
            </nav>
          </div>
        </div>
      )}
    </section>
  );
}

export default TableList;
